/**
 * Config API Client
 * File: src/api/configService.js
 *
 * Helper functions for interacting with the config API endpoints.
 * Uses the shared axios instance from api.js for consistent baseURL handling.
 */

import api from '../services/api';

/**
 * Fetch all funds from the master fund universe
 */
export async function fetchFunds() {
    const response = await api.get('/api/config/funds');
    return response.data.funds || [];
}

/**
 * Fetch all providers summary
 */
export async function fetchProviders() {
    const response = await api.get('/api/config/providers');
    return response.data;
}

/**
 * Fetch full provider config including allocation matrix
 */
export async function fetchProviderConfig(code) {
    const response = await api.get(`/api/config/providers/${code}`);
    return response.data;
}

/**
 * Fetch allocation matrix for a provider
 */
export async function fetchAllocationMatrix(code) {
    const response = await api.get(`/api/config/providers/${code}/allocation-matrix`);
    return response.data;
}

/**
 * Fetch risk bands for a provider
 */
export async function fetchRiskBands(code) {
    const response = await api.get(`/api/config/providers/${code}/risk-bands`);
    return response.data;
}

/**
 * Fetch all questions (tolerance + capacity)
 */
export async function fetchQuestions() {
    const response = await api.get('/api/config/questions');
    return response.data;
}

/**
 * Fetch tolerance questions only
 */
export async function fetchToleranceQuestions() {
    const response = await api.get('/api/config/questions/tolerance');
    return response.data;
}

/**
 * Fetch capacity questions only
 */
export async function fetchCapacityQuestions() {
    const response = await api.get('/api/config/questions/capacity');
    return response.data;
}

/**
 * Fetch all settings
 */
export async function fetchSettings() {
    const response = await api.get('/api/config/settings');
    return response.data;
}

/**
 * Fetch settings by category
 */
export async function fetchSettingsByCategory(category) {
    const response = await api.get(`/api/config/settings/${category}`);
    return response.data;
}

// ============================================================================
// ADMIN ENDPOINTS (for saving config)
// ============================================================================

/**
 * Save provider config
 */
export async function saveProviderConfig(code, data) {
    const response = await api.post(`/api/admin/config/providers/${code}`, { data });
    return response.data;
}

/**
 * Save all settings
 */
export async function saveSettings(data) {
    const response = await api.post('/api/admin/config/settings', { data });
    return response.data;
}

/**
 * Save questions config
 */
export async function saveQuestions(data) {
    const response = await api.post('/api/admin/config/questions', { data });
    return response.data;
}

/**
 * Reload config from disk
 */
export async function reloadConfig() {
    const response = await api.post('/api/admin/config/reload');
    return response.data;
}

/**
 * Validate all config
 */
export async function validateConfig() {
    const response = await api.get('/api/admin/config/validate');
    return response.data;
}

// ============================================================================
// COMBINED LOADERS (for convenience)
// ============================================================================

/**
 * Load all config needed for the admin UI
 */
export async function loadAdminConfig() {
    const [funds, providers, settings, questions] = await Promise.all([
        fetchFunds(),
        fetchProviders(),
        fetchSettings(),
        fetchQuestions()
    ]);

    return { funds, providers, settings, questions };
}

/**
 * Load all config needed for the questionnaire
 */
export async function loadQuestionnaireConfig() {
    const [questions, settings] = await Promise.all([
        fetchQuestions(),
        fetchSettingsByCategory('risk')
    ]);

    return { questions, settings };
}

export default {
    fetchFunds,
    fetchProviders,
    fetchProviderConfig,
    fetchAllocationMatrix,
    fetchRiskBands,
    fetchQuestions,
    fetchToleranceQuestions,
    fetchCapacityQuestions,
    fetchSettings,
    fetchSettingsByCategory,
    saveProviderConfig,
    saveSettings,
    saveQuestions,
    reloadConfig,
    validateConfig,
    loadAdminConfig,
    loadQuestionnaireConfig
};